import { useMemo, useState } from 'react';
import {
  App as AntApp,
  Card,
  ConfigProvider,
  Empty,
  Input,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import {
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { useOpticsQuery, useUnblockOpticMutation, type OpticRead } from './hooks';
import { BlockOpticModal } from './BlockOpticModal';

const { Title, Text } = Typography;

// Палитра прототипа (design/platform/standalone.html, screen==='optics').
const HEADER_BG = '#F5F8FB';
const BORDER = '#E3EAF0';
const SELECTED_BG = '#EAF8F7';
const INK = '#0E3559';
const MUTED = '#5A7388';
const FAINT = '#94A9BD';
const ROW_BORDER = '#EEF2F6';

const DASH = '—';

// Статус-бейдж (пилюля с точкой): активна — зелёная, заблокирована — красная.
function StatusBadge({ active, label }: { active: boolean; label: string }): JSX.Element {
  const c = active
    ? { bg: '#EAF8F7', fg: '#1FA9A4', dot: '#2DBDB8' }
    : { bg: '#FBE3E8', fg: '#9C2B3F', dot: '#D8556B' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 9px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: c.dot }} />
      {label}
    </span>
  );
}

// Экран «Оптики» (M5.7) — справочник подключённых оптик и их блокировка.
// Мастер-деталь: слева таблица (/platform/optics), справа sticky-карточка с
// контактами и тумблером активности. Расход SMS сюда больше не входит — он в
// SMS-центре. Блокировка — через модалку с обязательной причиной.
export function OpticsPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data, isLoading, error } = useOpticsQuery();
  const unblockMutation = useUnblockOpticMutation();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [blockTargetId, setBlockTargetId] = useState<string | null>(null);

  const optics = useMemo(() => data?.items ?? [], [data]);

  const rows = useMemo<OpticRead[]>(() => {
    const q = search.trim().toLowerCase();
    return optics
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      .filter(
        (o) =>
          !q ||
          o.name.toLowerCase().includes(q) ||
          (o.city ?? '').toLowerCase().includes(q) ||
          (o.address ?? '').toLowerCase().includes(q),
      );
  }, [optics, search]);

  // Выбранную оптику берём из свежих данных, чтобы карточка обновлялась после
  // блокировки/разблокировки (list инвалидируется мутацией).
  const selected = useMemo<OpticRead | null>(
    () => optics.find((o) => o.tenant_id === selectedId) ?? null,
    [optics, selectedId],
  );

  const blockTarget = useMemo<OpticRead | null>(
    () => optics.find((o) => o.tenant_id === blockTargetId) ?? null,
    [optics, blockTargetId],
  );

  const handleToggle = (optic: OpticRead, nextActive: boolean): void => {
    if (nextActive) {
      // Заблокирована → включаем сразу, без модалки.
      unblockMutation.mutate(
        { tenantId: optic.tenant_id },
        {
          onSuccess: () => void message.success(t('platform.optics.toast_unblocked')),
        },
      );
    } else {
      // Активна → выключаем через модалку с обязательной причиной.
      setBlockTargetId(optic.tenant_id);
    }
  };

  const columns: ColumnsType<OpticRead> = [
    {
      title: t('platform.optics.col_optic'),
      key: 'name',
      render: (_v, o) => (
        <Text strong style={{ fontSize: 13, color: o.is_active ? INK : FAINT }}>
          {o.name}
        </Text>
      ),
    },
    {
      title: t('platform.optics.col_city'),
      key: 'city',
      width: 150,
      render: (_v, o) => <Text style={{ fontSize: 12, color: MUTED }}>{o.city ?? DASH}</Text>,
    },
    {
      title: t('platform.optics.col_address'),
      key: 'address',
      render: (_v, o) => <Text style={{ fontSize: 12, color: MUTED }}>{o.address ?? DASH}</Text>,
    },
    {
      title: t('platform.optics.col_phone'),
      key: 'phone',
      width: 150,
      render: (_v, o) => (
        <Text className="sl-mono" style={{ fontSize: 12, color: MUTED }}>
          {o.phone ?? DASH}
        </Text>
      ),
    },
    {
      title: t('platform.optics.col_status'),
      key: 'status',
      width: 150,
      render: (_v, o) => (
        <StatusBadge
          active={o.is_active}
          label={
            o.is_active ? t('platform.optics.status_active') : t('platform.optics.status_blocked')
          }
        />
      ),
    },
  ];

  return (
    <div>
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('platform.optics.title')}
          </Title>
          <Text type="secondary">{t('platform.optics.subtitle')}</Text>
        </div>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: FAINT }} />}
          placeholder={t('platform.optics.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          data-testid="optics-search"
        />
      </Space>

      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 360px',
          gap: 18,
          alignItems: 'start',
        }}
        className="sl-optics-grid"
      >
        <Card variant="outlined" styles={{ body: { padding: 0 } }}>
          <ConfigProvider
            theme={{ components: { Table: { headerBg: HEADER_BG, borderColor: BORDER } } }}
          >
            <Table<OpticRead>
              rowKey="tenant_id"
              loading={isLoading}
              dataSource={rows}
              columns={columns}
              pagination={false}
              size="middle"
              onRow={(o) => ({
                onClick: () => setSelectedId(o.tenant_id),
                style: {
                  cursor: 'pointer',
                  background: o.tenant_id === selectedId ? SELECTED_BG : undefined,
                },
              })}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      search.trim()
                        ? t('platform.optics.empty_search', { query: search.trim() })
                        : t('platform.optics.empty')
                    }
                  />
                ),
              }}
            />
          </ConfigProvider>
        </Card>

        <div style={{ position: 'sticky', top: 0 }}>
          <OpticDetail
            optic={selected}
            onToggle={handleToggle}
            toggleLoading={unblockMutation.isPending}
          />
        </div>
      </div>

      <BlockOpticModal
        open={blockTarget !== null}
        tenantId={blockTargetId}
        opticName={blockTarget?.name ?? ''}
        onClose={() => setBlockTargetId(null)}
      />
    </div>
  );
}

interface OpticDetailProps {
  optic: OpticRead | null;
  onToggle: (optic: OpticRead, nextActive: boolean) => void;
  toggleLoading: boolean;
}

// Sticky-карточка выбранной оптики: контакты + тумблер активности + причина
// блокировки. Пустое состояние — подсказка «выберите оптику».
function OpticDetail({ optic, onToggle, toggleLoading }: OpticDetailProps): JSX.Element {
  const { t } = useTranslation();

  if (!optic) {
    return (
      <Card variant="outlined">
        <div style={{ padding: '30px 10px', textAlign: 'center', color: FAINT }}>
          <ShopOutlined style={{ fontSize: 34, marginBottom: 10 }} />
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>{t('platform.optics.detail_empty')}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="outlined" data-testid="optic-detail">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25, color: INK }}>
            {optic.name}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
            {(optic.city ?? DASH) + ` · ${t('platform.optics.id_label')} `}
            <span className="sl-mono">{optic.tenant_id.slice(0, 8)}</span>
          </div>
        </div>
        <StatusBadge
          active={optic.is_active}
          label={
            optic.is_active
              ? t('platform.optics.status_active')
              : t('platform.optics.status_blocked')
          }
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <InfoRow
          icon={<EnvironmentOutlined />}
          label={t('platform.optics.info_address')}
          value={optic.address}
        />
        <InfoRow
          icon={<PhoneOutlined />}
          label={t('platform.optics.info_phone')}
          value={optic.phone}
          mono
        />
        <InfoRow
          icon={<UserOutlined />}
          label={t('platform.optics.info_admin')}
          value={optic.admin_name}
        />
        <InfoRow
          icon={<MailOutlined />}
          label={t('platform.optics.info_email')}
          value={optic.admin_email}
          mono
          last
        />
      </div>

      {/* Карточка-тумблер активности. */}
      <div
        style={{
          marginTop: 16,
          padding: '14px 16px',
          borderRadius: 12,
          background: optic.is_active ? '#F5F8FB' : '#FBE3E8',
          border: `1px solid ${optic.is_active ? ROW_BORDER : '#F2C4CE'}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: INK }}>
            {optic.is_active
              ? t('platform.optics.toggle_active_title')
              : t('platform.optics.toggle_blocked_title')}
          </div>
          <div style={{ fontSize: 11, lineHeight: 1.4, color: MUTED, marginTop: 3 }}>
            {optic.is_active
              ? t('platform.optics.toggle_active_subtitle')
              : t('platform.optics.toggle_blocked_subtitle')}
          </div>
        </div>
        <Switch
          checked={optic.is_active}
          loading={toggleLoading}
          onChange={(next) => onToggle(optic, next)}
          data-testid="optic-toggle"
        />
      </div>

      {/* Причина блокировки — только у заблокированной с заполненной причиной. */}
      {!optic.is_active && optic.block_reason && (
        <div
          style={{
            marginTop: 12,
            padding: '14px 16px',
            borderRadius: 12,
            background: '#fff',
            border: '1px solid #F2C4CE',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#9C2B3F',
              marginBottom: 8,
            }}
          >
            <WarningOutlined /> {t('platform.optics.block_reason_heading')}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: INK }}>{optic.block_reason}</div>
        </div>
      )}
    </Card>
  );
}

interface InfoRowProps {
  icon: JSX.Element;
  label: string;
  value: string | null;
  mono?: boolean;
  last?: boolean;
}

function InfoRow({ icon, label, value, mono, last }: InfoRowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '11px 0',
        borderBottom: last ? undefined : `1px solid ${ROW_BORDER}`,
      }}
    >
      <span style={{ color: FAINT, flexShrink: 0, fontSize: 16 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: FAINT,
            marginBottom: 3,
          }}
        >
          {label}
        </div>
        <div
          className={mono ? 'sl-mono' : undefined}
          style={{ fontSize: 13, lineHeight: 1.35, color: INK, wordBreak: 'break-word' }}
        >
          {value ?? DASH}
        </div>
      </div>
    </div>
  );
}
